/* eslint-disable consistent-return */
/**
 *
 * Client Exclusive Card
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Card from 'components/Card';
import _get from 'lodash/get';
import { fallbackImage, getImageFile, isIE } from 'utils/stringUtils';
import ClassNames from 'classnames';
import CardActionArea from '@material-ui/core/CardActionArea';
import { useHistory, useLocation } from 'react-router-dom';
import Mixpanel from 'utils/mixpanelService';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import RightArrow from './images/blue-right-arrow.svg';
import NewTabLink from './images/new-tab.svg';

const useStyles = makeStyles(theme => ({
  image: {
    display: 'block',
    width: '100%',
    height: 'auto',
    position: 'relative',
    overflow: 'hidden',
    padding: '56.25% 0 0 0',
    borderBottom: '1px solid #E9ECED',
  },
  originalImage: {
    display: 'block',
    width: '100%',
    height: 'auto',
    margin: 'auto',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  borderedImage: {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  article: {
    display: '-webkit-box',
    marginTop: 17,
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: 17,
    fontFamily: 'PoppinsSemiBold',
    height: 41,
  },
  btnWrapper: {
    paddingLeft: 20,
    display: 'flex',
    paddingBottom: 15,
  },
  actionBtn: {
    border: '1px solid #E1E1E1',
    borderRadius: 2,
    width: 108,
    height: 34,
    ...theme.typography.pxSmall,
    color: theme.palette.primary.main,
    lineHeight: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  linkIcon: {
    height: 12,
    marginRight: 5,
    marginBottom: 1,
  },
  itemWrapper: {
    position: 'relative',
  },
  indicator: {
    height: 5,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  description: {
    display: 'block',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    height: 40,
  },
  cta: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 12,
    margin: '20px 0',
    height: 18,
    '& img': {
      marginLeft: 5,
    },
  },
  shortCta: {
    margin: '10px 0 15px 0',
  },
  item: {
    width: '100%',
    padding: '0 20px',
  },
  topicItem: {
    padding: '0 14px',
  },
  cardLink: {
    textDecoration: 'none',
  },
}));

function ClientExclusiveCard(props) {
  const { item, clientData, page = 'Home', type = '' } = props;
  const { primaryColor } = clientData;
  const isTopicPage = page === 'Topic';
  const showSubtitle = ['Topic', 'User-Resources'].includes(page);
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const {
    __typename,
    article,
    name,
    url,
    slug,
    callToAction,
    shortDescription,
    subtitle,
  } = item;
  const file = getImageFile(item);
  const internalSource = __typename === 'Source' && !url.includes('http');
  const isInternal =
    (url && (url.includes('crediblemind.com') || url.startsWith('/'))) ||
    internalSource;
  const isActivity =
    _get(item, 'sys.contentType.sys.id') === 'activity' ||
    __typename === 'Activity' ||
    type === 'activity';
  const showAsLink = isIE() && !isInternal && url && !isActivity;
  // redirect
  const redirect = () => {
    const pageName = location.pathname.split('/')[1];
    const clientName = clientData.name;
    const finalPageName =
      pageName === 'landing' ? `${pageName}Page` : 'homePage';
    const articleUrl = isActivity && `/articles/${slug}`;

    if (isInternal || isActivity) {
      const resourceUrl = isActivity
        ? articleUrl
        : url.replace('https://crediblemind.com', '');
      Mixpanel.track(`Client Resource Card Clicked - ${page}`, {
        article: article || name,
        page: finalPageName,
        slug,
        resource: resourceUrl,
        client: clientName,
      });
    } else {
      Mixpanel.track(`Client Resource Card Exited - ${page}`, {
        page: location.pathname,
        exitPage: url,
        client: clientName,
      });
    }
    if (showAsLink) return false;
    if (!isInternal && url && !isActivity) {
      const finalUrl = url.includes('http') ? url : `https://${url}`;
      window.open(finalUrl, '_blank', 'noopener');
    } else {
      const redirectUrl = isActivity
        ? articleUrl
        : url.replace('https://crediblemind.com', '');
      history.push(redirectUrl);
    }
  };

  const content = () => (
    <Card
      background={isTopicPage ? '#F8F8F8' : '#fff'}
      padding={0}
      borderRadius={8}
      borderColor="#E9ECED"
      mb={isTopicPage ? 20 : 0}
      width={isTopicPage ? '100%' : ''}
    >
      <CardActionArea
        onClick={() => redirect(item)}
        className={classes.itemWrapper}
      >
        {primaryColor && (
          <Grid
            item
            xs={12}
            className={classes.indicator}
            style={{ background: `#${primaryColor}` }}
          />
        )}
        <Grid item xs={12} className={classes.image}>
          <LazyLoadImage
            src={file}
            onError={e => {
              e.target.src = fallbackImage;
            }}
            alt="Client Exclusive"
            className={ClassNames(
              classes.originalImage,
              !primaryColor ? classes.borderedImage : null,
            )}
          />
        </Grid>
        <Grid
          item
          xs={12}
          className={ClassNames(classes.item, {
            [classes.topicItem]: isTopicPage,
          })}
        >
          <Typography
            variant="h6"
            color="textPrimary"
            align="left"
            className={ClassNames(classes.article)}
          >
            {article || name}
          </Typography>
        </Grid>
        {showSubtitle && (
          <Grid
            item
            xs={12}
            className={ClassNames(classes.item, {
              [classes.topicItem]: isTopicPage,
            })}
          >
            <Typography
              align="left"
              color="textPrimary"
              variant="overline"
              className={classes.description}
            >
              {shortDescription || subtitle}
            </Typography>
          </Grid>
        )}

        <Grid
          item
          xs={12}
          className={ClassNames(classes.item, {
            [classes.topicItem]: isTopicPage,
          })}
        >
          <Typography
            align="left"
            color="textSecondary"
            className={ClassNames(classes.cta, {
              [classes.shortCta]: page === 'Home',
            })}
          >
            {callToAction || 'Learn More'}
            <img src={isActivity ? RightArrow : NewTabLink} alt="" />
          </Typography>
        </Grid>
      </CardActionArea>
    </Card>
  );
  if (showAsLink)
    return (
      <a href={url} target="_blank" className={classes.cardLink}>
        {content()}
      </a>
    );
  return content();
}

ClientExclusiveCard.propTypes = {
  item: PropTypes.object,
  clientData: PropTypes.object,
  primaryColor: PropTypes.string,
  page: PropTypes.string,
  type: PropTypes.string,
};

export default React.memo(ClientExclusiveCard);
